import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useI18nContext } from '../../contexts/I18nContext'

// Add custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  .dark .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #64748b;
  }
  .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #475569;
  }
`

interface PillGeneratorProps {
  onPillChange: (pillData: PillData) => void
  initialPillData?: PillData
}

export interface PillData {
  shape: string
  color: string
  pattern: string
  shapeScale: number
  patternScale: number
  patternThickness: number
  // New gradient properties
  gradient?: {
    enabled: boolean
    colors: string[]
    direction: number
  }
  svg: string
}

const PillGenerator: React.FC<PillGeneratorProps> = ({ onPillChange, initialPillData }) => {
  
  // Collapsible sections state
  const [shapesExpanded, setShapesExpanded] = useState(true)
  const [colorsExpanded, setColorsExpanded] = useState(true)
  const [patternsExpanded, setPatternsExpanded] = useState(true)

  // Simple chevron icons
  const ChevronDownIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )

  const ChevronUpIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  )

  // Pill state
  const [pillShape, setPillShape] = useState(initialPillData?.shape || 'round')
  const [pillColor, setPillColor] = useState(initialPillData?.color || '#ffffff')
  const [pillPattern, setPillPattern] = useState(initialPillData?.pattern || 'none')
  const [pillShapeScale, setPillShapeScale] = useState(initialPillData?.shapeScale || 1)
  const [pillPatternScale, setPillPatternScale] = useState(initialPillData?.patternScale || 1)
  const [pillPatternThickness, setPillPatternThickness] = useState(initialPillData?.patternThickness || 1)
  
  // New advanced controls
  const [gradientEnabled, setGradientEnabled] = useState(initialPillData?.gradient?.enabled || false)
  const [gradientColors, setGradientColors] = useState(initialPillData?.gradient?.colors || ['#ffffff', '#f0f0f0'])
  const [gradientDirection, setGradientDirection] = useState(initialPillData?.gradient?.direction || 45)
  
  const { t } = useI18nContext()
  
  const shapes = {
    'round': { name: t('shape-round'), path: 'M50,50 m-40,0 a40,40 0 1,1 80,0 a40,40 0 1,1 -80,0' },
    'oval': { name: t('shape-oval'), path: 'M50,50 m-35,0 a35,25 0 1,1 70,0 a35,25 0 1,1 -70,0' },
    'square': { name: t('shape-square'), path: 'M20,20 L80,20 L80,80 L20,80 Z' },
    'rectangle': { name: t('shape-rectangle'), path: 'M15,35 L85,35 L85,65 L15,65 Z' },
    'capsule': { name: t('shape-capsule'), path: 'M30,20 L70,20 A15,15 0 0,1 70,80 L30,80 A15,15 0 0,1 30,20 Z' },
    'diamond': { name: t('shape-diamond'), path: 'M50,15 L80,50 L50,85 L20,50 Z' },
    'hexagon': { name: t('shape-hexagon'), path: 'M35,20 L65,20 L80,50 L65,80 L35,80 L20,50 Z' },
    'triangle': { name: t('shape-triangle'), path: 'M50,15 L80,75 L20,75 Z' },
    'heart': { name: t('shape-heart'), path: 'M50,25 C50,20 45,15 40,15 C35,15 30,20 30,25 C30,30 35,35 50,50 C65,35 70,30 70,25 C70,20 65,15 60,15 C55,15 50,20 50,25 Z' },
    'syringe': { name: t('shape-vaccine'), path: 'M20,45 L25,45 L25,40 L30,40 L30,35 L75,35 L75,40 L80,40 L80,60 L75,60 L75,65 L30,65 L30,60 L25,60 L25,55 L20,55 Z M75,42 L78,42 L78,58 L75,58 Z' },
    'serum': { name: t('shape-serum'), path: 'M40,15 L60,15 L65,20 L65,75 L60,80 L40,80 L35,75 L35,20 Z M45,10 L55,10 L55,15 L45,15 Z M50,8 L50,12' },
    'drop': { name: t('shape-drop'), path: 'M50,15 C50,15 35,35 35,55 C35,65 42,75 50,75 C58,75 65,65 65,55 C65,35 50,15 50,15 Z' },
    'bottle': { name: t('shape-bottle'), path: 'M35,25 L35,80 L65,80 L65,25 L60,25 L60,20 L55,20 L55,15 L45,15 L45,20 L40,20 L40,25 Z M45,10 L55,10 L55,15 L45,15 Z' },
    'powder': { name: t('shape-powder'), path: 'M25,30 L75,30 L75,70 L25,70 Z M30,25 L70,25 L70,30 L30,30 Z M35,35 C37,35 37,37 35,37 M45,40 C47,40 47,42 45,42 M55,38 C57,38 57,40 55,40 M40,50 C42,50 42,52 40,52 M60,48 C62,48 62,50 60,50' },
    'inhaler': { name: t('shape-vaper'), path: 'M30,40 L30,60 L45,60 L45,65 L55,65 L55,60 L70,60 L70,40 Z M40,35 L60,35 L60,40 L40,40 Z M47,25 L53,25 L53,35 L47,35 Z' },
    'effervescent': { name: t('shape-effervescent'), path: 'M50,50 m-35,0 a35,35 0 1,1 70,0 a35,35 0 1,1 -70,0 M45,40 C47,40 47,42 45,42 M55,35 C57,35 57,37 55,37 M40,50 C42,50 42,52 40,52 M60,45 C62,45 62,47 60,47 M50,60 C52,60 52,62 50,62' },
    'ampoule': { name: t('shape-ampoule'), path: 'M40,25 L60,25 L65,30 L65,70 L60,75 L40,75 L35,70 L35,30 Z M45,15 L55,15 L55,25 L45,25 Z M47,10 L53,10 L53,15 L47,15 Z' },
    'patch': { name: t('shape-patch'), path: 'M25,35 L75,35 L75,65 L25,65 Z M30,30 L70,30 L70,35 L30,35 Z M30,65 L70,65 L70,70 L30,70 Z' },
    'spray': { name: t('shape-spray'), path: 'M35,50 L35,75 L45,75 L45,80 L55,80 L55,75 L65,75 L65,50 L60,50 L60,45 L55,45 L55,25 L60,25 L60,20 L40,20 L40,25 L45,25 L45,45 L40,45 L40,50 Z M50,15 C52,17 54,17 56,15' },
    'tube': { name: t('shape-cream'), path: 'M35,35 L65,35 L70,40 L70,70 L65,75 L35,75 L30,70 L30,40 Z M40,25 L60,25 L60,35 L40,35 Z M45,20 L55,20 L55,25 L45,25 Z' },
    'nebulizer': { name: t('shape-nebulizer'), path: 'M30,45 L70,45 L70,65 L30,65 Z M40,35 L60,35 L60,45 L40,45 Z M45,25 L55,25 L55,35 L45,35 Z M35,40 C37,42 39,42 41,40 M45,40 C47,42 49,42 51,40 M55,40 C57,42 59,42 61,40' },
    'injection': { name: t('shape-injection'), path: 'M45,15 L55,15 L55,30 L70,30 L70,35 L75,35 L75,40 L70,40 L70,45 L55,45 L55,60 L45,60 L45,45 L30,45 L30,40 L25,40 L25,35 L30,35 L30,30 L45,30 Z' },
    'vitamin': { name: t('shape-vitamin'), path: 'M35,25 L65,25 L70,30 L70,70 L65,75 L35,75 L30,70 L30,30 Z M40,35 L45,45 L50,35 L55,45 L60,35 M40,55 L60,55 M45,65 L55,65' }
  }

  // Enhanced color palette with medical colors (reduced grays)
  const colorPalette = [
    // Row 1 - Essential grays and vibrant colors
    '#ffffff', '#e9ecef', '#6c757d', '#212529', // Solo 4 grises esenciales
    '#ff0000', '#dc3545', '#fd7e14', '#ffc107', '#28a745', '#20c997',
    '#17a2b8', '#007bff', '#6610f2', '#6f42c1', '#e83e8c', '#fd1d7c',
    '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7', '#dda0dd',
    // Row 2 - Medical and pastel colors
    '#ffebee', '#e8f5e8', '#e3f2fd', '#fff3e0', '#f3e5f5', '#fce4ec', 
    '#fff8e1', '#e0f2f1', '#f1f8e9', '#ffcdd2', '#c8e6c9', '#bbdefb', 
    '#ffe0b2', '#d1c4e9', '#f8bbd9', '#fff9c4', '#b2dfdb', '#dcedc8', 
    '#ef9a9a', '#a5d6a7', '#81c784', '#64b5f6'
  ]

  const patterns = {
    'none': { name: t('pattern-none') },
    'line': { name: t('pattern-line') },
    'cross': { name: t('pattern-cross') },
    'dots': { name: t('pattern-dots') },
    'stripe': { name: t('pattern-striped') },
    'plus': { name: t('pattern-plus') },
    'score': { name: t('pattern-groove') },
    'spiral': { name: t('pattern-spiral') },
    'diamond-pattern': { name: t('pattern-diamonds') },
    'waves': { name: t('pattern-waves') }
  }

  const generateGradient = (colors: string[], direction: number): string => {
    if (!gradientEnabled || colors.length < 2) return colors[0] || '#ffffff'
    return `url(#gradient-${direction})`
  }

  const generateGradientDef = (colors: string[], direction: number): string => {
    if (!gradientEnabled || colors.length < 2) return ''
    
    const angle = (direction * Math.PI) / 180
    const x1 = 50 + 50 * Math.cos(angle + Math.PI)
    const y1 = 50 + 50 * Math.sin(angle + Math.PI)
    const x2 = 50 + 50 * Math.cos(angle)
    const y2 = 50 + 50 * Math.sin(angle)

    return `
      <defs>
        <linearGradient id="gradient-${direction}" x1="${x1}%" y1="${y1}%" x2="${x2}%" y2="${y2}%">
          <stop offset="0%" style="stop-color:${colors[0]};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${colors[1]};stop-opacity:1" />
        </linearGradient>
      </defs>
    `
  }

  const generateAdvancedPattern = (pattern: string, patternScale: number, fillColor: string, thickness: number = 1): string => {
    const borderColor = gradientEnabled ? '#999999' : getDarkerColor(fillColor)
    
    /*
     * CONTROLES INDEPENDIENTES:
     * - patternScale: Afecta el TAMAÑO/LONGITUD del patrón (tamaño de elementos, espaciado, longitud)
     * - thickness: Afecta solo el GROSOR de las líneas/bordes del patrón
     */
    
    switch (pattern) {
      case 'spiral':
        const spiralPath = generateSpiralPath(patternScale)
        return `<path d="${spiralPath}" stroke="${borderColor}" stroke-width="${Math.max(1, 2 * thickness)}" fill="none"/>`
      
      case 'diamond-pattern':
        const diamondSize = Math.max(8, 15 * patternScale)
        const diamondStroke = Math.max(1, thickness)
        let diamonds = ''
        for (let x = 20; x <= 80; x += diamondSize * 2) {
          for (let y = 20; y <= 80; y += diamondSize * 2) {
            diamonds += `<path d="M${x},${y-diamondSize/2} L${x+diamondSize/2},${y} L${x},${y+diamondSize/2} L${x-diamondSize/2},${y} Z" stroke="${borderColor}" stroke-width="${diamondStroke}" fill="none"/>`
          }
        }
        return diamonds
      
      case 'waves':
        const waveHeight = Math.max(5, 10 * patternScale)
        const waveStroke = Math.max(1, 2 * thickness)
        let waves = ''
        for (let y = 30; y <= 70; y += waveHeight * 2) {
          waves += `<path d="M20,${y} Q35,${y-waveHeight} 50,${y} T80,${y}" stroke="${borderColor}" stroke-width="${waveStroke}" fill="none"/>`
        }
        return waves
      
      default:
        return generateOldPattern(pattern, patternScale, borderColor, thickness)
    }
  }

  const generateSpiralPath = (scale: number): string => {
    let path = 'M50,50'
    const turns = 3 * scale
    const maxRadius = 25 * scale
    const steps = 100
    
    for (let i = 1; i <= steps; i++) {
      const angle = (i / steps) * turns * 2 * Math.PI
      const radius = (i / steps) * maxRadius
      const x = 50 + radius * Math.cos(angle)
      const y = 50 + radius * Math.sin(angle)
      path += ` L${x},${y}`
    }
    return path
  }

  const generateOldPattern = (pattern: string, patternScale: number, borderColor: string, thickness: number = 1): string => {
    switch (pattern) {
      case 'line':
        const lineStroke = Math.max(1, 2 * thickness) // Solo thickness afecta grosor
        const lineLength = Math.max(30, 60 * patternScale) // patternScale afecta longitud
        const lineStart = 50 - (lineLength / 2)
        const lineEnd = 50 + (lineLength / 2)
        return `<line x1="50" y1="${lineStart}" x2="50" y2="${lineEnd}" stroke="${borderColor}" stroke-width="${lineStroke}"/>`
      case 'cross':
        const crossStroke = Math.max(1, 2 * thickness) // Solo thickness afecta grosor
        const crossOffset = 15 * patternScale // patternScale afecta tamaño
        return `
          <line x1="${50 - crossOffset}" y1="${50 - crossOffset}" x2="${50 + crossOffset}" y2="${50 + crossOffset}" stroke="${borderColor}" stroke-width="${crossStroke}"/>
          <line x1="${50 + crossOffset}" y1="${50 - crossOffset}" x2="${50 - crossOffset}" y2="${50 + crossOffset}" stroke="${borderColor}" stroke-width="${crossStroke}"/>
        `
      case 'dots':
        const dotRadius = Math.max(1, 2 * patternScale + thickness) // Combinado para puntos
        const dotOffset = 10 * patternScale
        return `
          <circle cx="${50 - dotOffset}" cy="${50 - dotOffset}" r="${dotRadius}" fill="${borderColor}"/>
          <circle cx="${50 + dotOffset}" cy="${50 - dotOffset}" r="${dotRadius}" fill="${borderColor}"/>
          <circle cx="50" cy="${50 + dotOffset}" r="${dotRadius}" fill="${borderColor}"/>
        `
      case 'stripe':
        const stripeStroke = Math.max(0.5, 1 * thickness) // Solo thickness afecta grosor
        const stripeSpacing = 10 * patternScale // patternScale afecta espaciado
        let stripes = ''
        for (let i = 0; i < Math.ceil(6 / patternScale); i++) {
          const y = 25 + (i * stripeSpacing)
          if (y <= 75) {
            stripes += `<line x1="30" y1="${y}" x2="70" y2="${y}" stroke="${borderColor}" stroke-width="${stripeStroke}"/>`
          }
        }
        return stripes
      case 'plus':
        const plusStroke = Math.max(1, 2 * thickness) // Solo thickness afecta grosor
        const plusLength = 15 * patternScale // patternScale afecta longitud
        return `
          <line x1="50" y1="${50 - plusLength}" x2="50" y2="${50 + plusLength}" stroke="${borderColor}" stroke-width="${plusStroke}"/>
          <line x1="${50 - plusLength}" y1="50" x2="${50 + plusLength}" y2="50" stroke="${borderColor}" stroke-width="${plusStroke}"/>
        `
      case 'score':
        const scoreStroke = Math.max(1, 3 * thickness) // Solo thickness afecta grosor
        const scoreLength = Math.max(40, 60 * patternScale) // patternScale afecta longitud
        const scoreStart = 50 - (scoreLength / 2)
        const scoreEnd = 50 + (scoreLength / 2)
        return `<line x1="${scoreStart}" y1="50" x2="${scoreEnd}" y2="50" stroke="${borderColor}" stroke-width="${scoreStroke}" opacity="0.6"/>`
      default:
        return ''
    }
  }

  const getDarkerColor = (color: string): string => {
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16)
      const g = parseInt(color.slice(3, 5), 16)
      const b = parseInt(color.slice(5, 7), 16)
      return `rgb(${Math.max(0, r - 40)}, ${Math.max(0, g - 40)}, ${Math.max(0, b - 40)})`
    }
    return '#999999'
  }

  const generatePillSVG = (shape: string, color: string, pattern: string, size = 60, shapeScaleVal = 1, patternScaleVal = 1, patternThickness = 1): string => {
    const shapeData = shapes[shape as keyof typeof shapes]
    
    if (!shapeData) {
      return getDefaultPillSVG(size)
    }

    // Use SVG transform for scaling, centered at (50, 50)
    const scaleTransform = shapeScaleVal !== 1 ? 
      `transform="translate(50, 50) scale(${shapeScaleVal}) translate(-50, -50)"` : ''
    
    const fillColor = gradientEnabled ? generateGradient(gradientColors, gradientDirection) : color
    const borderColor = getDarkerColor(color)
    
    let patternElements = ''
    if (pattern !== 'none') {
      patternElements = generateAdvancedPattern(pattern, patternScaleVal, color, patternThickness)
    }

    const gradientDef = generateGradient(gradientColors, gradientDirection).includes('url') ? 
      generateGradientDef(gradientColors, gradientDirection) : ''

    return `
      <svg width="${size}" height="${size}" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        ${gradientDef}
        <g>
          <path d="${shapeData.path}" 
                fill="${fillColor}" 
                stroke="${borderColor}" 
                stroke-width="2"
                ${scaleTransform}/>
          ${patternElements}
        </g>
      </svg>
    `
  }

  const getDefaultPillSVG = (size = 60): string => {
    return `
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
      </svg>
    `
  }

  const generatePatternSVG = (pattern: string, size = 32): string => {
    const borderColor = '#6b7280'
    const fillColor = '#f3f4f6'
    const patternColor = '#374151'
    
    const baseShape = `<rect x="4" y="4" width="24" height="24" rx="4" fill="${fillColor}" stroke="${borderColor}" stroke-width="1"/>`
    
    let patternElement = ''
    
    switch (pattern) {
      case 'line':
        patternElement = `<line x1="16" y1="8" x2="16" y2="24" stroke="${patternColor}" stroke-width="2"/>`
        break
      case 'cross':
        patternElement = `
          <line x1="12" y1="12" x2="20" y2="20" stroke="${patternColor}" stroke-width="2"/>
          <line x1="20" y1="12" x2="12" y2="20" stroke="${patternColor}" stroke-width="2"/>
        `
        break
      case 'dots':
        patternElement = `
          <circle cx="12" cy="12" r="1.5" fill="${patternColor}"/>
          <circle cx="20" cy="12" r="1.5" fill="${patternColor}"/>
          <circle cx="16" cy="20" r="1.5" fill="${patternColor}"/>
        `
        break
      case 'stripe':
        patternElement = `
          <line x1="8" y1="10" x2="24" y2="10" stroke="${patternColor}" stroke-width="1"/>
          <line x1="8" y1="14" x2="24" y2="14" stroke="${patternColor}" stroke-width="1"/>
          <line x1="8" y1="18" x2="24" y2="18" stroke="${patternColor}" stroke-width="1"/>
          <line x1="8" y1="22" x2="24" y2="22" stroke="${patternColor}" stroke-width="1"/>
        `
        break
      case 'plus':
        patternElement = `
          <line x1="16" y1="10" x2="16" y2="22" stroke="${patternColor}" stroke-width="2"/>
          <line x1="10" y1="16" x2="22" y2="16" stroke="${patternColor}" stroke-width="2"/>
        `
        break
      case 'score':
        patternElement = `<line x1="8" y1="16" x2="24" y2="16" stroke="${patternColor}" stroke-width="3"/>`
        break
      case 'spiral':
        patternElement = `<path d="M16,12 Q20,12 20,16 Q20,20 16,20 Q12,20 12,16 Q12,14 14,14" stroke="${patternColor}" stroke-width="1.5" fill="none"/>`
        break
      case 'diamond-pattern':
        patternElement = `
          <path d="M12,16 L16,12 L20,16 L16,20 Z" stroke="${patternColor}" stroke-width="1" fill="none"/>
          <path d="M16,12 L18,10 L20,12 L18,14 Z" stroke="${patternColor}" stroke-width="1" fill="none"/>
        `
        break
      case 'waves':
        patternElement = `
          <path d="M8,14 Q12,10 16,14 T24,14" stroke="${patternColor}" stroke-width="1.5" fill="none"/>
          <path d="M8,18 Q12,14 16,18 T24,18" stroke="${patternColor}" stroke-width="1.5" fill="none"/>
        `
        break
      case 'none':
        // Just show a clean rectangle for "none" pattern
        patternElement = `<text x="16" y="20" text-anchor="middle" font-size="8" fill="${patternColor}" font-family="Arial">SIN</text>`
        break
      default:
        // For any unimplemented patterns, show a question mark
        patternElement = `<text x="16" y="20" text-anchor="middle" font-size="12" fill="${patternColor}" font-family="Arial">?</text>`
        break
    }
    
    return `
      <svg width="${size}" height="${size}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        ${baseShape}
        ${patternElement}
      </svg>
    `
  }

  // Update pill data whenever any value changes
  useEffect(() => {
    const svg = generatePillSVG(pillShape, pillColor, pillPattern, 60, pillShapeScale, pillPatternScale, pillPatternThickness)
    const pillData: PillData = {
      shape: pillShape,
      color: pillColor,
      pattern: pillPattern,
      shapeScale: pillShapeScale,
      patternScale: pillPatternScale,
      patternThickness: pillPatternThickness,
      gradient: {
        enabled: gradientEnabled,
        colors: gradientColors,
        direction: gradientDirection
      },
      svg
    }
    onPillChange(pillData)
  }, [pillShape, pillColor, pillPattern, pillShapeScale, pillPatternScale, pillPatternThickness, gradientEnabled, gradientColors, gradientDirection])

  const handleShapeSelect = (shape: string) => {
    setPillShape(shape)
  }

  const handlePatternSelect = (pattern: string) => {
    setPillPattern(pattern)
  }

  const currentSvg = generatePillSVG(pillShape, pillColor, pillPattern, 60, pillShapeScale, pillPatternScale, pillPatternThickness)

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />
      <motion.div 
        className="pill-generator border-t border-gray-200 dark:border-gray-600 pt-4 mt-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        
        {/* Sticky Preview for Mobile - Always visible at top */}
        <motion.div 
          className="sticky top-0 z-10 bg-white dark:bg-gray-800 pb-2 mb-4 lg:hidden"
          variants={itemVariants}
        >
          <div className="flex items-center justify-center">
            <motion.div 
              className="flex items-center justify-center w-16 h-16"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ 
                scale: 1, 
                opacity: 1,
                transition: {
                  type: "spring" as const,
                  stiffness: 200,
                  damping: 20
                }
              }}
              key={`${pillShape}-${pillColor}-${pillPattern}`}
              whileHover={{ 
                scale: 1.1,
                transition: { duration: 0.4 }
              }}
            >
              <motion.div 
                dangerouslySetInnerHTML={{ __html: currentSvg }}
                animate={{
                  scale: [1, 1.05, 1],
                  transition: {
                    duration: 0.3,
                    ease: "easeInOut" as const
                  }
                }}
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Responsive Layout: Side-by-side on larger screens, single column on mobile */}
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          {/* Preview Section - Only visible on larger screens */}
          <div className="hidden lg:block lg:col-span-1">
            {/* Sticky Preview */}
            <motion.div 
              className="sticky top-4 flex items-center justify-center mb-4"
              variants={itemVariants}
            >
              <motion.div 
                className="flex items-center justify-center w-20 h-20"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ 
                  scale: 1, 
                  opacity: 1,
                  transition: {
                    type: "spring" as const,
                    stiffness: 200,
                    damping: 20
                  }
                }}
                key={`${pillShape}-${pillColor}-${pillPattern}`}
                whileHover={{ 
                  scale: 1.1,
                  transition: { duration: 0.4 }
                }}
              >
                <motion.div 
                  dangerouslySetInnerHTML={{ __html: currentSvg }}
                  animate={{
                    scale: [1, 1.05, 1],
                    transition: {
                      duration: 0.3,
                      ease: "easeInOut" as const
                    }
                  }}
                />
              </motion.div>
            </motion.div>
          </div>

          {/* Controls Section */}
          <div className="lg:col-span-2">
            {/* Editor Content */}
            <motion.div variants={itemVariants}>
              <motion.div className="space-y-4">

              {/* Shape Selection - Collapsible */}
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setShapesExpanded(!shapesExpanded)
                        }}
                        className="flex items-center space-x-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded p-1"
                      >
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('forms')} ({Object.keys(shapes).length})
                        </span>
                        <motion.div
                          animate={{ rotate: shapesExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {shapesExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </motion.div>
                      </button>
                    </div>
                    
                    {/* Shape Size Slider - same line */}
                    <div className="flex items-center space-x-2 ml-4">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {t('size')}
                      </span>
                      <motion.input 
                        type="range" 
                        min="0.5" 
                        max="1.5" 
                        step="0.1" 
                        value={pillShapeScale}
                        onChange={(e) => setPillShapeScale(parseFloat(e.target.value))}
                        className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                        whileHover={{ scale: 1.02 }}
                        whileFocus={{ scale: 1.02 }}
                      />
                      <motion.span 
                        className="text-xs text-gray-500 min-w-0"
                        key={pillShapeScale}
                        initial={{ opacity: 0.5 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {Math.round(pillShapeScale * 100)}%
                      </motion.span>
                    </div>
                  </div>
                </div>
                
                <AnimatePresence>
                  {shapesExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="p-3 border-t border-gray-200 dark:border-gray-600">
                        <div 
                          className="overflow-x-auto custom-scrollbar" 
                          style={{ 
                            scrollbarWidth: "thin",
                            scrollbarColor: "#cbd5e1 transparent"
                          }}
                        >
                          <div className="flex gap-2 pb-2" style={{ minWidth: "fit-content" }}>
                            {Object.entries(shapes).map(([key, shape], index) => (
                              <motion.button 
                                key={`shape-${key}`}
                                type="button" 
                                className={`p-2 w-16 h-16 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary flex-shrink-0 ${key === pillShape ? 'ring-2 ring-primary bg-primary/10' : ''}`} 
                                onClick={() => handleShapeSelect(key)}
                                initial={{ scale: 1 }}
                                whileHover={{ 
                                  scale: 1.05,
                                  transition: { type: "spring" as const, stiffness: 400, damping: 10 }
                                }}
                                whileTap={{ 
                                  scale: 0.95,
                                  transition: { duration: 0.1 }
                                }}
                                style={{
                                  boxShadow: key === pillShape ? "0 0 0 2px rgb(59 130 246 / 0.5)" : undefined
                                }}
                                title={shape.name}
                              >
                                <div className="w-full h-full flex items-center justify-center">
                                  <div dangerouslySetInnerHTML={{ __html: generatePillSVG(key, '#d1d5db', 'none', 32, 1, 1, 1) }} />
                                </div>
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Color Selection - Collapsible */}
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setColorsExpanded(!colorsExpanded)
                  }}
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {gradientEnabled ? t('gradient') : `${t('colors')} (${colorPalette.length})`}
                    </span>
                    <div
                      onClick={(e) => {
                        e.stopPropagation()
                        setGradientEnabled(!gradientEnabled)
                      }}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors cursor-pointer ${
                        gradientEnabled 
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                      }`}
                    >
                      {gradientEnabled ? '🎨 ON' : t('gradient')}
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: colorsExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {colorsExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </motion.div>
                </button>
                
                <AnimatePresence>
                  {colorsExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="p-3 border-t border-gray-200 dark:border-gray-600">
                        {gradientEnabled ? (
                          // Controles de Gradiente
                          <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                  {t('direction')}: {gradientDirection}°
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max="360"
                                  value={gradientDirection}
                                  onChange={(e) => setGradientDirection(parseInt(e.target.value))}
                                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                  {t('gradient-colors')}
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                  <input
                                    type="color"
                                    value={gradientColors[0]}
                                    onChange={(e) => setGradientColors([e.target.value, gradientColors[1]])}
                                    className="w-full h-8 rounded border border-gray-300 dark:border-gray-600"
                                  />
                                  <input
                                    type="color"
                                    value={gradientColors[1]}
                                    onChange={(e) => setGradientColors([gradientColors[0], e.target.value])}
                                    className="w-full h-8 rounded border border-gray-300 dark:border-gray-600"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Selector de Colores Normal
                          <div 
                            className="overflow-x-auto custom-scrollbar" 
                            style={{ 
                              scrollbarWidth: "thin",
                              scrollbarColor: "#cbd5e1 transparent"
                            }}
                          >
                            <div className="flex gap-2 pb-2" style={{ minWidth: "fit-content" }}>
                              {colorPalette.map((color, index) => (
                                <motion.button 
                                  key={`color-${index}`}
                                  type="button" 
                                  className={`p-2 w-16 h-16 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary text-xs flex-shrink-0 ${color === pillColor ? 'ring-2 ring-primary bg-primary/10' : ''}`} 
                                  onClick={() => setPillColor(color)}
                                  initial={{ scale: 1 }}
                                  whileHover={{ 
                                    scale: 1.05,
                                    transition: { type: "spring" as const, stiffness: 400, damping: 10 }
                                  }}
                                  whileTap={{ 
                                    scale: 0.95,
                                    transition: { duration: 0.1 }
                                  }}
                                  style={{
                                    boxShadow: color === pillColor ? "0 0 0 2px rgb(59 130 246 / 0.5)" : undefined
                                  }}
                                  title={color}
                                >
                                  <div className="w-full h-full flex items-center justify-center">
                                    <div style={{ width: '16px', height: '16px', backgroundColor: color, borderRadius: '4px' }} />
                                  </div>
                                </motion.button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>


              {/* Pattern Selection - Collapsible */}
              <div className="border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setPatternsExpanded(!patternsExpanded)
                        }}
                        className="flex items-center space-x-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded p-1"
                      >
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('patterns')} ({Object.keys(patterns).length})
                        </span>
                        <motion.div
                          animate={{ rotate: patternsExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          {patternsExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </motion.div>
                      </button>
                    </div>
                    
                    {/* Pattern Controls - same line */}
                    <div className="flex items-center space-x-4 ml-4">
                      {/* Pattern Size Slider */}
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          {t('size')}
                        </span>
                        <motion.input 
                          type="range" 
                          min="0.5" 
                          max="2" 
                          step="0.1" 
                          value={pillPatternScale}
                          onChange={(e) => setPillPatternScale(parseFloat(e.target.value))}
                          className="w-16 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          whileHover={{ scale: 1.02 }}
                          whileFocus={{ scale: 1.02 }}
                        />
                        <motion.span 
                          className="text-xs text-gray-500"
                          key={pillPatternScale}
                          initial={{ opacity: 0.5 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          {Math.round(pillPatternScale * 100)}%
                        </motion.span>
                      </div>
                      
                      {/* Pattern Thickness Slider */}
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          {t('thickness')}
                        </span>
                        <motion.input 
                          type="range" 
                          min="0.5" 
                          max="3" 
                          step="0.1" 
                          value={pillPatternThickness}
                          onChange={(e) => setPillPatternThickness(parseFloat(e.target.value))}
                          className="w-16 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          whileHover={{ scale: 1.02 }}
                          whileFocus={{ scale: 1.02 }}
                        />
                        <motion.span 
                          className="text-xs text-gray-500"
                          key={pillPatternThickness}
                          initial={{ opacity: 0.5 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        >
                          {Math.round(pillPatternThickness * 100)}%
                        </motion.span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <AnimatePresence>
                  {patternsExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="p-3 border-t border-gray-200 dark:border-gray-600">
                        <div 
                          className="overflow-x-auto custom-scrollbar" 
                          style={{ 
                            scrollbarWidth: "thin",
                            scrollbarColor: "#cbd5e1 transparent"
                          }}
                        >
                          <div className="flex gap-2 pb-2" style={{ minWidth: "fit-content" }}>
                            {Object.entries(patterns).map(([key, pattern], index) => (
                              <motion.button 
                                key={`pattern-${key}`}
                                type="button" 
                                className={`p-2 w-16 h-16 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary text-xs flex-shrink-0 ${key === pillPattern ? 'ring-2 ring-primary bg-primary/10' : ''}`} 
                                onClick={() => handlePatternSelect(key)}
                                initial={{ scale: 1 }}
                                whileHover={{ 
                                  scale: 1.05,
                                  transition: { type: "spring" as const, stiffness: 400, damping: 10 }
                                }}
                                whileTap={{ 
                                  scale: 0.95,
                                  transition: { duration: 0.1 }
                                }}
                                style={{
                                  boxShadow: key === pillPattern ? "0 0 0 2px rgb(59 130 246 / 0.5)" : undefined
                                }}
                                title={pattern.name}
                              >
                                <div className="w-full h-full flex items-center justify-center">
                                  <div dangerouslySetInnerHTML={{ __html: generatePatternSVG(key, 32) }} />
                                </div>
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </>
  )
}

export default PillGenerator 